import type { ModelInfo } from '@paradigm/terminal-core';

/**
 * Native model catalog — the source of truth is the OpenClaw gateway.
 *
 * Every terminal pod's sidecar exposes `GET /api/models` (region-pinned bedrock
 * ids for that cluster). Because the gateway owns the list, the picker works
 * for ANY gateway with no host injection: `<Terminal>` fetches this directly.
 * The endpoint is reachable cross-origin (the sidecar sets CORS headers and
 * auth is a bearer token, not a cookie), so a browser on a different origin
 * than the gateway still gets it. Until it resolves (or if it fails), the
 * picker shows the native offline fallback (see `getStaticModels`).
 *
 * Friendly display names are also native (see `defaultModelLabel`), since every
 * host shares the same gateway catalog.
 */

/** `wss://x.dime.gg/agent` → `https://x.dime.gg`; null if unparseable. */
export function deriveHttpBase(agentUrl: string | null): string | null {
  if (agentUrl == null || agentUrl.length === 0) return null;
  try {
    const u = new URL(agentUrl);
    const protocol =
      u.protocol === 'wss:'
        ? 'https:'
        : u.protocol === 'ws:'
          ? 'http:'
          : u.protocol;
    if (protocol !== 'https:' && protocol !== 'http:') return null;
    return `${protocol}//${u.host}`;
  } catch {
    return null;
  }
}

function coerceModels(json: unknown): readonly ModelInfo[] {
  if (json == null || typeof json !== 'object') return [];
  const { models } = json as { readonly models?: unknown };
  if (!Array.isArray(models)) return [];
  const out: ModelInfo[] = [];
  for (const entry of models as readonly unknown[]) {
    if (entry == null || typeof entry !== 'object') continue;
    const rec = entry as {
      readonly id?: unknown;
      readonly label?: unknown;
      readonly legacy?: unknown;
    };
    if (typeof rec.id !== 'string' || typeof rec.label !== 'string') continue;
    out.push({
      id: rec.id,
      label: rec.label,
      ...(rec.legacy === true ? { legacy: true } : {}),
    });
  }
  // The gateway returns models in an unspecified order (the sidecar reads them
  // from a map, so the order is not stable across refreshes). Sort by the real
  // model id — not the prettified label — so the picker order is deterministic.
  out.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  return out;
}

/**
 * Fetch the live model catalog from the gateway pod's sidecar. Returns `[]` on
 * any failure (network, non-200, bad payload, or missing auth) so callers fall
 * back to the static list without an extra try/catch.
 *
 * Authenticates exactly like the session-sync client: pass `getAuthHeaders` and
 * its returned `Authorization: Bearer <gateway-token>` (+ `X-Sync-User`) headers
 * are sent on the request. The k8s sidecar gates `/api/models` with the same
 * token-mode auth as `/sync/v1`, so when `getAuthHeaders` yields no token we
 * skip the request (the caller keeps the static list) rather than fire one that
 * 401s. No `credentials: 'include'` — auth is the bearer header, mirroring the
 * sync client (and avoiding the wildcard-CORS credentials trap).
 */
export async function fetchGatewayModels(
  agentUrl: string | null,
  options?: {
    readonly signal?: AbortSignal;
    readonly getAuthHeaders?: () => Readonly<Record<string, string>> | null;
  },
): Promise<readonly ModelInfo[]> {
  const base = deriveHttpBase(agentUrl);
  if (base == null) return [];
  const auth = options?.getAuthHeaders?.();
  // Caller supplied an auth source but there's no token yet ⇒ skip; the fetch
  // would only 401. (No getAuthHeaders at all ⇒ unauthenticated gateway, fetch
  // as-is.)
  if (options?.getAuthHeaders != null && auth == null) return [];
  try {
    const res = await fetch(`${base}/api/models`, {
      method: 'GET',
      headers: { Accept: 'application/json', ...(auth ?? {}) },
      signal: options?.signal,
    });
    if (!res.ok) return [];
    const json = (await res.json()) as unknown;
    return coerceModels(json);
  } catch {
    return [];
  }
}
